// src/components/common/EmptyState.tsx
import * as React from 'react';
import { FileQuestion, Search, Inbox, FolderOpen, Users, MessageSquare, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

type EmptyStateVariant = 'default' | 'search' | 'inbox' | 'folder' | 'users' | 'messages' | 'tickets';

interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const variantIcons: Record<EmptyStateVariant, React.ReactNode> = {
  default: <FileQuestion className="h-12 w-12" />,
  search: <Search className="h-12 w-12" />,
  inbox: <Inbox className="h-12 w-12" />,
  folder: <FolderOpen className="h-12 w-12" />,
  users: <Users className="h-12 w-12" />,
  messages: <MessageSquare className="h-12 w-12" />,
  tickets: <Ticket className="h-12 w-12" />,
};

function EmptyState({
  variant = 'default',
  title,
  description,
  icon,
  action,
  className,
}: EmptyStateProps) {
  const IconComponent = icon || variantIcons[variant];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className
      )}
    >
      <div className="text-gray-400 mb-4">{IconComponent}</div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-gray-500 max-w-sm mb-4">{description}</p>
      )}
      {action && (
        <Button variant="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

// Componentes pre-configurados para casos comunes
function NoSearchResults({ query, onClear }: { query?: string; onClear?: () => void }) {
  return (
    <EmptyState
      variant="search"
      title="Sin resultados"
      description={
        query
          ? `No se encontraron resultados para "${query}"`
          : 'No se encontraron resultados para tu búsqueda'
      }
      action={onClear ? { label: 'Limpiar búsqueda', onClick: onClear } : undefined}
    />
  );
}

function NoData({ entityName, onCreate }: { entityName: string; onCreate?: () => void }) {
  return (
    <EmptyState
      variant="inbox"
      title={`No hay ${entityName}`}
      description={`Aún no se han creado ${entityName}. Comienza creando uno nuevo.`}
      action={onCreate ? { label: `Crear ${entityName}`, onClick: onCreate } : undefined}
    />
  );
}

export { EmptyState, NoSearchResults, NoData };
export type { EmptyStateProps };
